import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/context/AuthContext';
import {
  Search,
  UserPlus,
  Loader2,
  Trash2,
  Pencil,
  Check,
  X,
  ShieldCheck,
  Clock3,
  Ban,
} from 'lucide-react';

const ROLES = ['admin', 'manager', 'agent', 'employee'];
const STATUSES = ['pending', 'active', 'blocked'];

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    const okQ = !s || u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
    const okS = filterStatus === 'all' || u.status === filterStatus;
    return okQ && okS;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      setUsers((all) => all.filter((u) => u.id !== id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      const { data } = await axios.patch(`${API}/admin/users/${id}`, updates);
      setUsers((all) => all.map((u) => (u.id === id ? { ...u, ...data } : u)));
      setEditing(null);
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Update failed');
    }
  };

  const quickApprove = (id) => handleUpdate(id, { status: 'active' });
  const quickBlock = (id) => handleUpdate(id, { status: 'blocked' });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-600 text-sm">Approve requests, assign roles and manage access.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-white font-semibold hover:brightness-110 transition"
          style={{ backgroundColor: '#1e73be' }}
        >
          <UserPlus className="w-4 h-4" /> New User
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 flex flex-wrap items-center gap-2 border-b border-slate-100">
          <div className="flex-1 min-w-[200px] flex items-center border border-slate-200 rounded-md overflow-hidden focus-within:border-[#1e73be]">
            <span className="px-3 h-9 grid place-items-center text-slate-400"><Search className="w-4 h-4" /></span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email"
              className="flex-1 h-9 outline-none text-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : err ? (
          <div className="p-8 text-center text-red-600">{err}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Joined</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => {
                  const isEditing = editing?.id === u.id;
                  return (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full grid place-items-center bg-[#eaf3fb] text-[#1e73be] font-semibold flex-none">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{u.name}</p>
                            <p className="text-slate-500 truncate text-xs">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={editing.role}
                            onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                            className="h-8 px-2 rounded border border-slate-200 text-sm bg-white"
                          >
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        ) : (
                          <span className="inline-block text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                            {u.role}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={editing.status}
                            onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                            className="h-8 px-2 rounded border border-slate-200 text-sm bg-white"
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <StatusBadge status={u.status} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="inline-flex gap-1">
                            <IconBtn onClick={() => handleUpdate(u.id, { role: editing.role, status: editing.status })} title="Save">
                              <Check className="w-4 h-4" />
                            </IconBtn>
                            <IconBtn onClick={() => setEditing(null)} title="Cancel">
                              <X className="w-4 h-4" />
                            </IconBtn>
                          </div>
                        ) : (
                          <div className="inline-flex gap-1">
                            {u.status === 'pending' && (
                              <IconBtn onClick={() => quickApprove(u.id)} title="Approve" tone="green">
                                <ShieldCheck className="w-4 h-4" />
                              </IconBtn>
                            )}
                            {u.status !== 'blocked' && u.status !== 'pending' && (
                              <IconBtn onClick={() => quickBlock(u.id)} title="Block" tone="amber">
                                <Ban className="w-4 h-4" />
                              </IconBtn>
                            )}
                            <IconBtn onClick={() => setEditing({ id: u.id, role: u.role, status: u.status })} title="Edit">
                              <Pencil className="w-4 h-4" />
                            </IconBtn>
                            <IconBtn onClick={() => handleDelete(u.id)} title="Delete" tone="red">
                              <Trash2 className="w-4 h-4" />
                            </IconBtn>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={(u) => setUsers((all) => [u, ...all])} />}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    active: { bg: '#dcf7e7', fg: '#166534', icon: ShieldCheck },
    pending: { bg: '#fff6e0', fg: '#a15c00', icon: Clock3 },
    blocked: { bg: '#fee2e2', fg: '#991b1b', icon: Ban },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: m.bg, color: m.fg }}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const IconBtn = ({ onClick, title, tone, children }) => {
  const tones = {
    default: 'text-slate-600 hover:bg-slate-100',
    green: 'text-emerald-700 hover:bg-emerald-50',
    amber: 'text-amber-700 hover:bg-amber-50',
    red: 'text-red-700 hover:bg-red-50',
  };
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-8 h-8 grid place-items-center rounded-md ${tones[tone] || tones.default}`}
    >
      {children}
    </button>
  );
};

const CreateUserModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'employee', status: 'active' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/users`, form);
      onCreated(data);
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-[480px] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">Create user</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-11 px-3 border border-slate-200 rounded-md outline-none focus:border-[#1e73be]" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-11 px-3 border border-slate-200 rounded-md outline-none focus:border-[#1e73be]" />
          <input required type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-11 px-3 border border-slate-200 rounded-md outline-none focus:border-[#1e73be]" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 px-3 border border-slate-200 rounded-md bg-white outline-none focus:border-[#1e73be]">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-11 px-3 border border-slate-200 rounded-md bg-white outline-none focus:border-[#1e73be]">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {err && <p className="text-red-600 text-sm">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 h-10 rounded-md border border-slate-200 text-slate-700 font-medium">Cancel</button>
            <button type="submit" disabled={busy} className="px-4 h-10 rounded-md text-white font-semibold disabled:opacity-70" style={{ backgroundColor: '#1e73be' }}>
              {busy ? 'Creating...' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUsers;
